'use client';

import { CollapseController } from '@arckit/daisyui/headless';
import { Button, ButtonLink } from '@arckit/daisyui/primitives';
import type { ReactNode } from 'react';
import { Suspense } from 'react';
import { RiCodeBlock, RiMenuLine, RiQuestionFill } from 'react-icons/ri';
import { TrackerAction, TrackerCategory, trackEvent } from '@/configuration/telemetry/event-tracker';
import { inject } from '@/libraries/injection';
import { hrefWithSearchParams } from '@/libraries/nextjs';
import { useSearchParams } from '@/libraries/nextjs/shim';
import { Link } from '@/libraries/ui/primitives/link';
import { CONTACT_ACTION } from '@/shared/injection/keys/contact-action.key';
import { NAVBAR_CONFIG } from '../../injection/navbar-config.key';

type NavbarProps = {
  searchSlot?: ReactNode;
  filtersSlot?: ReactNode;
};

const HomeLink = ({
  homeUrl,
  logoUrl,
  appName
}: {
  homeUrl: string;
  logoUrl: string | undefined;
  appName: string | undefined;
}) => {
  const urlSearchParams = useSearchParams();
  const searchParams = new URLSearchParams(urlSearchParams);

  return (
    <Link
      href={hrefWithSearchParams(homeUrl)(searchParams, ['page'])}
      title="Retour à l'accueil"
      className='font-bold text-xl text-base-title flex items-center gap-2'
      kind='link-hover'
    >
      {/* biome-ignore lint/performance/noImgElement: next/image requires server access to public/ which is not available in standalone mode */}
      {logoUrl && <img src={logoUrl} alt='' width={40} height={40} className='bg-primary-subtle rounded-lg' />}
      {appName}
    </Link>
  );
};

const HomeLinkFallback = ({
  homeUrl,
  logoUrl,
  appName
}: {
  homeUrl: string;
  logoUrl: string | undefined;
  appName: string | undefined;
}) => (
  <Link
    href={homeUrl}
    title="Retour à l'accueil"
    className='font-bold text-xl text-base-title flex items-center gap-2'
    kind='link-hover'
  >
    {/* biome-ignore lint/performance/noImgElement: next/image requires server access to public/ which is not available in standalone mode */}
    {logoUrl && <img src={logoUrl} alt='' width={32} height={32} className='h-8' />}
    {appName}
  </Link>
);

export const Navbar = ({ searchSlot, filtersSlot }: NavbarProps) => {
  const { logoUrl, appName, helpLabel, homeUrl = '/' } = inject(NAVBAR_CONFIG);
  const ContactAction = inject(CONTACT_ACTION);

  const hasAppName = Boolean(appName);
  const hasBrand = Boolean(logoUrl) || hasAppName;

  return (
    <CollapseController>
      {({ toggle, collapsible }) => (
        <div className='px-8 py-4 shadow z-1'>
          <div className='flex gap-2 justify-between'>
            {hasBrand && (
              <Suspense fallback={<HomeLinkFallback homeUrl={homeUrl} logoUrl={logoUrl} appName={appName} />}>
                <HomeLink homeUrl={homeUrl} logoUrl={logoUrl} appName={appName} />
              </Suspense>
            )}
            <div className='flex'>
              <ContactAction kind='btn-link' className='no-underline'>
                <RiQuestionFill size={16} />
                {helpLabel ?? 'Aide'}
              </ContactAction>
              <ButtonLink
                href='https://tally.so/r/VLV5K6'
                kind='btn-link'
                className='no-underline'
                target='_blank'
                onClick={() => trackEvent({ category: TrackerCategory.EMBED, action: TrackerAction.EMBED_MAP_CLICK })}
              >
                <RiCodeBlock size={16} />
                Intégrer la carte sur votre site
              </ButtonLink>
              <Button kind='btn-link' className='px-2 lg:hidden' {...toggle}>
                <RiMenuLine size={24} aria-hidden={true} />
              </Button>
            </div>
          </div>
          <div className='mt-3 flex gap-2 flex-col 2xl:flex-row justify-between'>
            {searchSlot && <div>{searchSlot}</div>}
            {filtersSlot && (
              <div
                {...collapsible({
                  className: 'collapse lg:collapse-open w-fit'
                })}
              >
                <div className='collapse-content py-1! pl-0! pr-1!'>
                  <Suspense fallback={<div>Chargement...</div>}>{filtersSlot}</Suspense>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </CollapseController>
  );
};
